import React, {useEffect, useRef, useState} from "react";
import FilterItem from "./FilterItem";
import Icon from "../Icon/Icon";

import {
    InfiosClearFilter,
    InfiosFilter,
    InfiosFilterEffectWrapper,
    InfiosFilterTitle,
    InfiosFilterWrapper,
    InfiosTopWrapper,
    SkeletonWrapper,
} from "./Filter.style";
import axios from "axios";
import ReactPaginate from "react-paginate";
import {
    InfiosPaginationWrapper,
    InfiosSearchNotFound,
    InfiosSearchResults
} from "../SearchResults/SearchResults.style";
import Link from "../Link/Link";
import {urlHasParam} from "../../utils/js/params";
import ViewSwitch from "../ViewSwitch/ViewSwitch";
import ContentTeaserList from "../ContentTeaser/ContentTeaserList/ContentTeaserList";
import targetView from "../../utils/targetView";

function Filter(props) {
    const {filterTitle, fullWidth, showSearchResults, pageLanguage, showHighlightedDescription, isInEditor, hoverTextColor, textColor, hoverBackgroundColor, backgroundColor} = props;
    const {linkText, nextText, prevText, applyFilterText, closeText, clearFilterText, hidePagination} = props;

    const [content, setContent] = useState(null);
    const [isChanged, setChanged] = useState(false);
    const [currentOpen, setCurrentOpen] = useState(null);
    const [filtersSelected, setFiltersSelected] = useState({});
    const [tagCount, setTagCount] = useState({});
    const [resultsOffset, setResultsOffset] = useState(-1);
    const [pageRangeDisplayed, setPageRangeDisplayed] = useState(7)
    const [pageOffset, setPageOffset] = useState(0);
    const [totalResults, setTotalResults] = useState(0);
    const resultsLimit = showSearchResults ? 10 : 9;
    const searchTerm = showSearchResults ? new URLSearchParams(window.location.search).get("q") : null;
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [orderBy, setOrderBy] = useState('date');
    const [sortOrder, setSortOrder] = useState('desc');
    const [filterList, setFilterList] = useState(null);
    const [loadingResults, setLoadingResults] = useState(false);
    const [pagination, setPagination] = useState(null);
    const showPagination = !hidePagination;

    useEffect(() => {
        axios.get(`${props.cqPath}.infios-all-tags.json`).then(response => {
                setFilterList(props.filterOptions.map(option => {
                    let [parent, children] = option.split('/').slice(4);
                    children = children ? [children] : Object.keys(response.data[parent]).filter(f => response.data[parent][f]['jcr:primaryType'] === 'cq:Tag');

                    return {
                        parent: {...response.data[parent], name: parent},
                        children: children.map(c => ({...response.data[parent][c], name: parent + "/" + c}))
                    };
                }).reduce((acc, filter) => {
                    const {parent, children} = filter;

                    acc[parent.name] = {
                        parent,
                        children: [...(acc[parent.name]?.children || []), ...children]
                    };

                    return acc;
                }, {}));
            }
        )

        const pathName = window.location.pathname;
        const pageRegex = /-page(\d+)/;
        const pageMatch = pathName.match(pageRegex);
        let initialPage = 0;

        if (pageMatch && !isNaN(pageMatch[1]) && showPagination) {
          const parsedPage = parseInt(pageMatch[1], 10);
          initialPage = parsedPage - 1;
        }

        setPageOffset(initialPage);
        setResultsOffset(initialPage * resultsLimit);

        const loadedFilters = JSON.parse(sessionStorage.getItem('filterSelected'));
        if(loadedFilters && Object.keys(loadedFilters).length) {
          setFiltersSelected(loadedFilters);
          setChanged(true);
        }
    }, [])

    function initializeTagCount() {
        const initialTagCount = {};
        if(filterList){
            Object.values(filterList).forEach((item) => {
                item.children.forEach((subItem) => {
                    initialTagCount[subItem.name] = 0;
                });
            });

            setTagCount(initialTagCount);
        }
    }

    useEffect(() => {
        initializeTagCount();
    }, [filterList])

    useEffect(() => {
        const loadedFilters = JSON.parse(sessionStorage.getItem('filterSelected'));

        if (loadedFilters && Object.keys(filtersSelected).length === 0 && !Object.keys(loadedFilters).length) {
            setChanged(false);
        }

        sessionStorage.setItem('filterSelected', JSON.stringify(filtersSelected));
    }, [filtersSelected])

    useEffect(() => {
        if(filterList){
            getSearchResults(filtersSelected).then(response => {
                setContent(response.data.contentList);
                setTotalResults(response.data.scanCount);
                setTagCount(response.data.potentialCounts);
                setIsLoading(false);
            });
        }
    }, [orderBy, sortOrder])


    useEffect(() => {
        // workaround to make sure that it always shows only 7 indexes
        if (resultsOffset > 0 && resultsOffset < 6) {
            setPageRangeDisplayed(6)
        } else {
            setPageRangeDisplayed(7)
        }
    }, [resultsOffset])

    useEffect(() => {
        if(resultsOffset === -1) return;

        setIsLoading(true);
        setLoadingResults(true);

        if(filterList){
            getSearchResults(filtersSelected).then(response => {
                setTotalResults(response.data.scanCount);
                setContent(response.data.contentList);
                setTagCount(response.data.potentialCounts);
                getSpellCheck();
            }).finally(() => {
                setIsLoading(false)
                setLoadingResults(false);
            });
        }

    }, [filterList, resultsOffset])


    function changeOrder(orderBy, sortOrder) {
        setOrderBy(orderBy);
        setSortOrder(sortOrder);
    }

    const getBasePathAndExtension = (path) => {
      const lastDotIndex = path.lastIndexOf('.');
      if (lastDotIndex === -1) return { basePath: path, extension: '' };
      return {
        basePath: path.slice(0, lastDotIndex),
        extension: path.slice(lastDotIndex),
      };
    };

    const buildPaginationUrl = (page, pageCount) => {
      const pathName = window.location.pathname;
      const { basePath, extension } = getBasePathAndExtension(pathName);
      const currentUrlParams = new URLSearchParams(window.location.search);
      const queryString = currentUrlParams.toString();
      const pageRegex = /-page(\d+)/;

      return page >= 1 && page <= pageCount
        ? `${window.location.origin}${basePath.replace(pageRegex, "")}-page${page}${extension}${queryString ? '?' + queryString : ''}`
        : '#';
    };

    function onApply(type, filters) {
        setChanged(true);
        setResultsOffset(0);
        setPageOffset(0);

        if (filters.length) {
            setFiltersSelected((current) => {
                const updatedFilters = {
                    ...current, [type]: filters
                };

                return getContent(updatedFilters);
            });
        } else {
            setFiltersSelected((current) => {
                const {[type]: dynamicType, ...rest} = current;
                getContent(rest);
                return rest;
            });
        }
    }

    const getSearchResults = async (filters) => {
        if(resultsOffset === -1) return;

        const preSelected = Object.values(filterList)
            .filter((item) => item.children.length === 1)
            .reduce((result, item) => {
                result[item.parent.name] = [item.children[0].name];
                return result;
            }, {});

        setIsLoading(true);
        let selectedTags = Object.values({...preSelected, ...filters}).flat();
        const encodedTags = selectedTags.map(tag => encodeURIComponent(tag));
        const tagsQueryString = encodedTags.join("&tag=");
        const fullTextQuery = searchTerm ? `fulltext=${encodeURIComponent(searchTerm)}&` : '';

        let config = {
            method: "get",
            url: `${props.cqPath}.infios-content.json?${fullTextQuery}tag=${tagsQueryString}&resultsOffset=${resultsOffset}&resultsLimit=${resultsLimit}&orderBy=${orderBy}&sortOrder=${sortOrder}`
            ,
            headers: {
                "Content-Type": "application/json",
            },
        };

        return axios.request(config);
    }

    function getContent(filters) {
        if(filterList){
            getSearchResults(filters)
                .then((response) => {
                    setContent(response.data.contentList);
                    setTotalResults(response.data.scanCount);
                    setTagCount(response.data.potentialCounts);
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setIsLoading(false)
                });
            return filters;
        }
    }

    const getSpellCheck = async () => {
        if (!content || content.length === 0) {
            const urlParams = new URLSearchParams(window.location.search);
            const q = urlParams.get("q");
            axios.get(`${props.cqPath}.infios-suggestion.json?term=${q}&mode=spellcheck`)
                .then(response => {
                    setSuggestions(response.data);
                })
                .catch(error => {
                    console.error(error);
                });
        }
    }

    const contentRef = useRef(null);
    const wrapperRef = useRef(null);
    const shadowLeftRef = useRef(null);
    const shadowRightRef = useRef(null);

    useEffect(() => {
        const content = contentRef.current;
        const wrapper = wrapperRef.current;
        const shadowLeft = shadowLeftRef.current;
        const shadowRight = shadowRightRef.current;

        const updateScrollHeight = () => {
            const contentScrollHeight = content.scrollWidth - wrapper.offsetWidth;

            if (contentScrollHeight !== 0) {
                const currentScroll = content.scrollLeft / contentScrollHeight;
                shadowLeft.style.opacity = currentScroll;
                shadowRight.style.opacity = 1 - currentScroll;
            } else {
                shadowLeft.style.opacity = 0;
                shadowRight.style.opacity = 0;
            }
        };

        updateScrollHeight();

        content.addEventListener('scroll', updateScrollHeight);
        window.addEventListener('resize', updateScrollHeight);

        return () => {
            content.removeEventListener('scroll', updateScrollHeight);
        };
    }, []);

    function handleClearSelections() {
        setCurrentOpen(null);
        setFiltersSelected({});
        setTagCount({});
        setChanged(false);
        initializeTagCount();
        setResultsOffset(0);
        setPageOffset(0);
        getContent({});
    }

    useEffect(() => {
      const pathName = window.location.pathname;
      const totalPages = Math.ceil(totalResults / resultsLimit);
      let initialPage = 0;

      if (!totalPages) return;

      const pageRegex = /-page(\d+)/;
      const pageMatch = pathName.match(pageRegex);
      const { basePath, extension } = getBasePathAndExtension(pathName);

      if (pageMatch && !isNaN(pageMatch[1])) {
        const parsedPage = parseInt(pageMatch[1], 10);

        if (parsedPage >= 1 && parsedPage <= totalPages) {
          initialPage = parsedPage - 1;
        } else if ((parsedPage < 1 || parsedPage > totalPages) && showPagination) {
          const newUrl = `${basePath.replace(pageRegex, "")}-page1${extension}`;
          window.location.href = newUrl;
          return;
        }
      }

      setPageOffset(initialPage);
      setResultsOffset(initialPage * resultsLimit);

    }, [resultsLimit, totalResults, setPageOffset, setResultsOffset, showPagination]);

    const handlePaginationChange = (page) => {
      const selectedPage = page.selected;

      setResultsOffset(selectedPage * resultsLimit);
      setPageOffset(selectedPage);

      const newPageNumber = selectedPage + 1;
      const pathName = window.location.pathname;
      const { basePath, extension } = getBasePathAndExtension(pathName);
      const pageRegex = /-page(\d+)/;
      let newUrl;

      if (basePath.match(pageRegex)) {
        newUrl = basePath.replace(pageRegex, `-page${newPageNumber}`) + extension;
      } else {
        newUrl = `${basePath}-page${newPageNumber}${extension}`;
      }

      if (showPagination) window.location.href = newUrl;
      return;
    };

    useEffect(() => {
      if (!isLoading) {
        setPagination(
          <ReactPaginate
            breakLabel="..."
            nextLabel={nextText}
            previousLabel={prevText}
            onPageChange={(page) => {
              handlePaginationChange(page);
            }}
            pageCount={Math.ceil(totalResults / resultsLimit)}
            activeClassName="active"
            forcePage={pageOffset}
            hrefBuilder={(page, pageCount) => buildPaginationUrl(page, pageCount)}
            hrefAllControls={true}
          />
        );
      }
    }, [isLoading]);

    return (
      <>
        <InfiosFilter>
          <div className="filter">
            <InfiosTopWrapper fullWidth={fullWidth}>
              {filterTitle && <InfiosFilterTitle>{filterTitle}</InfiosFilterTitle>}
              {isChanged && (
                <InfiosClearFilter onClick={handleClearSelections}>
                  <span>{clearFilterText}</span>
                  <Icon icon="clear" />
                </InfiosClearFilter>
              )}
            </InfiosTopWrapper>
            <InfiosFilterEffectWrapper ref={wrapperRef} className="filter__wrapper_shadow" fullWidth={fullWidth}>
              <div ref={shadowLeftRef} className="filter__shadow shadow--left"></div>
              <div ref={shadowRightRef} className="filter__shadow shadow--right"></div>
              <InfiosFilterWrapper ref={contentRef} className={`filter__cotent_button ${currentOpen !== null ? 'open' : ""}`}>
                {filterList &&
                  Object.values(filterList).map((item, i) => (
                    <FilterItem
                      key={i}
                      tagCount={tagCount}
                      typeName={item.parent.name}
                      title={item.parent['jcr:title.' + pageLanguage] ?? item.parent['jcr:title']}
                      checkedItems={filtersSelected[item.parent.name]}
                      pageLanguage={pageLanguage}
                      items={item.children}
                      isOpen={currentOpen === i}
                      onOpen={() => setCurrentOpen(i)}
                      onClose={() => setCurrentOpen(null)}
                      onApply={onApply}
                      applyFilterText={applyFilterText}
                      closeText={closeText}
                      boxAlignment={
                        i === 0
                          ? 'start'
                          : i === Object.values(filterList).length - 1
                          ? 'end'
                          : 'middle'
                      }
                      hoverTextColor={hoverTextColor} 
                      textColor={textColor} 
                      hoverBackgroundColor={hoverBackgroundColor} 
                      backgroundColor={backgroundColor}
                      {...props}
                    />
                  ))}
              </InfiosFilterWrapper>
            </InfiosFilterEffectWrapper>
          </div>
        </InfiosFilter>

        {content && showSearchResults ? (
          <InfiosSearchResults id="resultsContainer" fullWidth={fullWidth}>
            <InfiosPaginationWrapper className="top-pagination" isLoading={isLoading || loadingResults}>
              {pagination}
            </InfiosPaginationWrapper>
            {content?.length > 0 && (
              <span className="heading-4">
                {totalResults} result{content.length !== 1 ? "s" : ""} found
              </span>
            )}
            {content && (
              <div className="content__list__itens">
                {content.map((result, index) => (
                  <ContentTeaserList
                    linkText={linkText}
                    key={`search-result-${index}`}
                    {...result}
                    term={searchTerm}

                  />
                ))}
              </div>
            )}
            {content?.length === 0 && (
              <InfiosSearchNotFound>
                <span className="heading-4">No results found</span>
                {suggestions && suggestions.length > 0 && (
                  <div className="search-suggestions">
                    <p className="p-regular">Did you mean:</p>
                    <div className="search-suggestions__link">
                      {suggestions.map((suggestion, index) => (
                        <Link
                          noIcon
                          key={`suggestion-search-${index}`}
                          link={`?q=${suggestion}${urlHasParam('wcmmode=disabled') ? '&wcmmode=disabled' : ''}`}
                        >
                          {suggestion}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </InfiosSearchNotFound>
            )}
            <InfiosPaginationWrapper className="bottom-pagination" isLoading={isLoading || loadingResults}>
              {pagination}
            </InfiosPaginationWrapper>
          </InfiosSearchResults>
        ) : (
          <>
            {!(isInEditor && !showPagination) && (
              <ViewSwitch
                fullWidth={fullWidth}
                contentList={content}
                linkText={linkText}
                isLoading={isLoading}
                orderBy={orderBy}
                sortOrder={sortOrder}
                totalResults={totalResults}
                showPagination={showPagination}
                changeOrder={changeOrder}
                showHighlightedDescription={showHighlightedDescription}
                handlePagination={number => handlePaginationChange({selected: number})}
                pageOffset={Math.ceil(pageOffset / resultsLimit)}
                pagination={pagination}
              />
            )}
          </>
        )}
      </>
    );

}

const FilterHandleErrors = props => {
    return (
            <Filter {...props}/>
    )
}

export default FilterHandleErrors;

